import { useRef } from "react"

import {
  useInstallIntegration,
  usePatchIntegration,
  useTestPackageRegistry
} from "@/api/useIntegrations"
import { INTEGRATION_VENDORS } from "@/const/vendors-constants"
import { useLogger } from "@/hooks/useLogger"
import { normalizeScope } from "@/pages/IntegrationsPage/helpers/packageRegistry"

import type {
  PackageRegistryEntryInput,
  RegistryConnectionStatus
} from "@/api/useIntegrations.types"
import type {
  PackageRegistryAPIProps,
  PackageRegistryFormValues
} from "./usePackageRegistryAPI.types"

const MESSAGE_PREFIX = "integrations.packageRegistry.messages"

export const toEntryInput = (
  values: PackageRegistryFormValues,
  id?: string
): PackageRegistryEntryInput => ({
  id,
  endpoint: id ? undefined : values.endpoint?.trim(),
  packageManagers: values.packageManagers || [],
  scopes: values.packageManagers?.includes("npm")
    ? (values.scopes || []).map(normalizeScope).filter(Boolean)
    : [],
  authMethod: values.authMethod,
  username: values.username?.trim() || undefined,
  secret: values.secret?.trim() || undefined,
  realm: values.packageManagers?.includes("sbt")
    ? values.realm?.trim() || undefined
    : undefined
})

export const usePackageRegistryAPI = (props: PackageRegistryAPIProps) => {
  const { entry, t, message: messageApi } = props
  const { logger, EVENTS } = useLogger()
  const events = EVENTS.ANALYTIC_EVENTS.INTEGRATIONS.PACKAGE_REGISTRY
  const vendor = INTEGRATION_VENDORS.packageRegistry
  const entryId = entry?.id
  const formId = entryId || `${vendor}-form`
  const installer = useInstallIntegration({ vendor })
  // Called directly rather than through useIntegration's onUpdate, which swallows errors.
  const updater = usePatchIntegration({ vendor })
  const tester = useTestPackageRegistry()
  // The last test run from this form: reported with the save, and warned about when failed.
  const lastTestStatus = useRef<RegistryConnectionStatus>("untested")

  const logEdit = (input: PackageRegistryEntryInput) => {
    if (!entry) return

    const before = entry.packageManagers
    const added = input.packageManagers.filter((pm) => !before.includes(pm))
    const removed = before.filter((pm) => !input.packageManagers.includes(pm))

    if (input.secret) {
      const setAt = entry.secretUpdatedAt || entry.createdAt
      logger.info(events.CREDENTIAL_REPLACED, {
        package_managers: input.packageManagers,
        days_since_previous: setAt
          ? Math.floor((Date.now() / 1000 - setAt) / 86400)
          : undefined
      })
    }
    if (added.length || removed.length)
      logger.info(events.FORMATS_EDITED, { added, removed })
    if (entry.migrated) logger.info(events.MIGRATED_EDITED)
  }

  const save = async (values: PackageRegistryFormValues) => {
    const prefix = `${MESSAGE_PREFIX}.${entryId ? "update" : "add"}`
    const input = toEntryInput(values, entryId)
    const payload = { data: { packageRegistry: input } }

    try {
      if (entryId) await updater.mutateAsync(payload)
      else await installer.mutateAsync(payload)

      if (entryId) logEdit(input)
      else
        logger.info(events.SAVED, {
          package_managers: input.packageManagers,
          auth_method: input.authMethod,
          scopes_count: input.scopes?.length || 0,
          test_result_at_save: lastTestStatus.current,
          endpoint_host: safeHost(input.endpoint)
        })

      const testFailed = !["untested", "connected"].includes(
        lastTestStatus.current
      )

      if (testFailed)
        messageApi?.warning(
          t("integrations.packageRegistry.configuration.testFailedWarning")
        )
      else messageApi?.success(t(`${prefix}.success`))

      return true
    } catch (error: any) {
      messageApi?.error(error?.data?.displayMessage || t(`${prefix}.failed`))

      return false
    }
  }

  const onFinish = async (values: PackageRegistryFormValues) => {
    const saved = await save(values)

    if (!saved) return

    if (!entryId) props.backToIntegration(true)
    else props.setIsDirty(false)
  }

  const onTest = async (values: PackageRegistryFormValues) => {
    const input = toEntryInput(values, entryId)

    try {
      messageApi?.loading(t("integrations.packageRegistry.test.testing"))

      const { data } = await tester.mutateAsync({ packageRegistry: input })

      messageApi?.destroy()
      lastTestStatus.current = data.status
      logger.info(events.TESTED, {
        package_managers: input.packageManagers,
        auth_method: input.authMethod,
        result: data.status
      })

      if (data.status === "connected") messageApi?.success(data.message)
      else messageApi?.error(data.message)

      return data.status
    } catch (error: any) {
      messageApi?.destroy()
      messageApi?.error(
        error?.data?.displayMessage ||
          t("integrations.packageRegistry.test.failed")
      )

      return undefined
    }
  }

  return {
    formId,
    entry,
    onFinish,
    onTest,
    isPending: installer.isPending || updater.isPending,
    isTesting: tester.isPending
  }
}

const safeHost = (endpoint?: string) => {
  try {
    return endpoint ? new URL(endpoint).host : undefined
  } catch {
    return undefined
  }
}
